import { type FormEvent, memo, useCallback } from 'react';
import { useStepperContext } from '@components/stepper';
import { useForm } from '@travelity/form/use-form';
import {
    ContentType,
    DiscountType,
    useAddProduct,
} from '@travelity/api/services';
import {
    type CreateProductReq2Dto,
    ListItemAction,
} from '@travelity/api/generated';
import { LoadingOverlay } from '@travelity/ui/loading-overlay';

import { useNavigate } from 'react-router-dom';
import type {
    PackageFormValues,
    PackageStepValue,
} from '../../add-package.types';
import {
    getFieldsForStep,
    getStepErrorMessages,
    parseHHmm,
} from '../../helpers';
import { packageFormDefaultValues } from '../../constants';

import PricingStep from '../PricingStep';
import AssetsStep from '../AssetsStep';
import { PackageNameDescription } from '../PackageNameDescription';
import { UnitProductsStep } from '../UnitProductsStep';
import { PackagePreviewStep } from '../PackagePreviewStep';

const Steps = () => {
    const navigate = useNavigate();
    const { activeIndex, steps, next, setShowErrors } =
        useStepperContext<PackageFormValues>();
    const { Form, trigger, getValues, formState } = useForm<PackageFormValues>({
        defaultValues: packageFormDefaultValues,
        validateInitially: true,
        mode: 'onChange',
    });
    const activeStep = steps[activeIndex];
    const { mutate: createPackage, isLoading } = useAddProduct();

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const values = getValues();
            const fields = getFieldsForStep(
                activeStep.value as PackageStepValue,
                values
            );
            const isValid = fields.length ? await trigger(fields as any) : true;

            if (!isValid) {
                setShowErrors(true);
                return;
            }

            setShowErrors(false);
            if (activeIndex < steps.length - 1) {
                next();
            }

            if (activeIndex === steps.length - 1) {
                const products: CreateProductReq2Dto['products'] =
                    values.products.map((product, i) => ({
                        day_index: i + 1,
                        offset_days: product.day_index - 1,
                        items: product.items.map((it, idx) => ({
                            id: undefined as any,
                            start_time: parseHHmm(it.start_time!),
                            ref: {
                                name: it.name,
                                type: it.type,
                                id: it.id,
                            },
                            order_index: idx,
                        })),
                    }));

                const packageSubmitForm: CreateProductReq2Dto = {
                    type: 'dynamic_package',
                    name: values.productDetails.name,
                    description: values.productDetails.description,
                    products,
                    discount: {
                        amount: values.discount.amount,
                        type: values.discount.type as DiscountType,
                    },
                    attachments: {
                        items: values.assets.images.map(attachment => ({
                            action: ListItemAction.ADD,
                            id: attachment.uuid!,
                            reference: {
                                name: attachment.name!,
                                content_type:
                                    attachment.contentType! as ContentType,
                                content: attachment.content!,
                                width: attachment.width!,
                                height: attachment.height!,
                            },
                        })),
                    },
                };
                createPackage(packageSubmitForm, {
                    onSuccess: () => {
                        navigate('/products', { replace: true });
                    },
                });
            }
        },
        [
            activeIndex,
            activeStep.value,
            getValues,
            next,
            setShowErrors,
            steps.length,
            trigger,
        ]
    );

    const stepFields = getFieldsForStep(
        activeStep.value as PackageStepValue,
        getValues()
    );
    const errors = getStepErrorMessages(formState.errors, stepFields);

    return (
        <Form id="step" onSubmit={handleSubmit}>
            {activeStep.value === 'product-details' && (
                <PackageNameDescription errors={errors} />
            )}
            {activeStep.value === 'unit-products' && (
                <UnitProductsStep errors={errors} />
            )}
            {activeStep.value === 'pricing' && <PricingStep errors={errors} />}
            {activeStep.value === 'assets' && <AssetsStep errors={errors} />}
            {activeStep.value === 'preview' && (
                <PackagePreviewStep errors={errors} />
            )}
            {isLoading && <LoadingOverlay />}
        </Form>
    );
};

export default memo(Steps);
